import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { ArrowLeft, AlertTriangle, ArrowUp, ArrowDown, RefreshCw } from 'lucide-react';
import { 
  usePaymentDiscrepancies, 
  usePendingDiscrepancyStats,
  useMarkDiscrepancyReviewed,
  useResolveDiscrepancy 
} from '@/hooks/use-payment-discrepancies';
import { 
  DiscrepancyStatus, 
  ResolutionType,
  ProcurementDiscrepancy 
} from '@/types/database/procurement-discrepancies';
import DiscrepancyList from '@/components/finance/DiscrepancyList';
import DiscrepancyResolutionDialog from '@/components/finance/DiscrepancyResolutionDialog';
import { formatCurrency } from '@/lib/utils';

const PaymentDiscrepanciesPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<DiscrepancyStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<'overpayment' | 'underpayment' | 'all'>('all');
  
  const [resolutionDialogOpen, setResolutionDialogOpen] = useState(false);
  const [selectedDiscrepancy, setSelectedDiscrepancy] = useState<ProcurementDiscrepancy | null>(null);

  const { data: discrepancies, isLoading, refetch } = usePaymentDiscrepancies({
    status: statusFilter,
    type: typeFilter,
  });

  const { data: stats } = usePendingDiscrepancyStats();
  const markReviewed = useMarkDiscrepancyReviewed();
  const resolveDiscrepancy = useResolveDiscrepancy();

  const handleMarkReviewed = async (id: string) => {
    await markReviewed.mutateAsync(id);
  };

  const handleOpenResolve = (id: string) => {
    const disc = discrepancies?.find(d => d.id === id);
    if (disc) {
      setSelectedDiscrepancy(disc);
      setResolutionDialogOpen(true);
    }
  };

  const handleResolve = async (resolutionType: ResolutionType, notes: string) => {
    if (!selectedDiscrepancy) return;
    await resolveDiscrepancy.mutateAsync({
      discrepancyId: selectedDiscrepancy.id,
      resolutionType,
      resolutionNotes: notes,
    });
    setResolutionDialogOpen(false);
    setSelectedDiscrepancy(null);
  };

  // Calculate summary stats from current filtered data
  const overpaymentTotal = discrepancies
    ?.filter(d => d.discrepancy_type === 'overpayment' && (statusFilter === 'all' || d.status === statusFilter))
    .reduce((sum, d) => sum + Math.abs(d.discrepancy_amount), 0) || 0;

  const underpaymentTotal = discrepancies
    ?.filter(d => d.discrepancy_type === 'underpayment' && (statusFilter === 'all' || d.status === statusFilter))
    .reduce((sum, d) => sum + Math.abs(d.discrepancy_amount), 0) || 0;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Payment Discrepancies</h1>
              <p className="text-muted-foreground">
                Review and resolve upfront payment variances
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Pending Alert */}
        {stats && stats.count > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="flex items-center gap-4 p-4">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-900">
                  {stats.count} pending {stats.count === 1 ? 'discrepancy requires' : 'discrepancies require'} attention
                </p>
                <p className="text-sm text-amber-700">
                  Total amount: {formatCurrency(stats.totalAmount)}
                </p>
              </div>
              <Button 
                variant="outline" 
                className="border-amber-300"
                onClick={() => setStatusFilter('pending')}
              >
                View Pending
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <ArrowUp className="h-4 w-4 text-orange-500" />
                Overpayments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-orange-600">
                {formatCurrency(overpaymentTotal)}
              </p>
              <p className="text-xs text-muted-foreground">
                {discrepancies?.filter(d => d.discrepancy_type === 'overpayment').length || 0} cases
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <ArrowDown className="h-4 w-4 text-blue-500" />
                Underpayments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(underpaymentTotal)}
              </p>
              <p className="text-xs text-muted-foreground">
                {discrepancies?.filter(d => d.discrepancy_type === 'underpayment').length || 0} cases
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Net Variance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${overpaymentTotal >= underpaymentTotal ? 'text-orange-600' : 'text-blue-600'}`}>
                {overpaymentTotal >= underpaymentTotal ? '+' : '-'}
                {formatCurrency(Math.abs(overpaymentTotal - underpaymentTotal))}
              </p>
              <p className="text-xs text-muted-foreground">
                Total {discrepancies?.length || 0} discrepancies
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Status:</span>
                <Select 
                  value={statusFilter} 
                  onValueChange={(v) => setStatusFilter(v as DiscrepancyStatus | 'all')}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewed">Under Review</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="waived">Waived</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Type:</span>
                <Select 
                  value={typeFilter} 
                  onValueChange={(v) => setTypeFilter(v as 'overpayment' | 'underpayment' | 'all')}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="overpayment">Overpayments</SelectItem>
                    <SelectItem value="underpayment">Underpayments</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(statusFilter !== 'all' || typeFilter !== 'all') && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setStatusFilter('all');
                    setTypeFilter('all');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Discrepancy List */}
        <DiscrepancyList
          discrepancies={discrepancies || []}
          isLoading={isLoading}
          onResolve={handleOpenResolve}
          onMarkReviewed={handleMarkReviewed}
          emptyMessage={
            statusFilter === 'pending' 
              ? 'No pending discrepancies - all caught up!' 
              : 'No discrepancies found matching your filters'
          }
        />

        {/* Resolution Dialog */}
        <DiscrepancyResolutionDialog
          open={resolutionDialogOpen}
          onOpenChange={setResolutionDialogOpen}
          discrepancy={selectedDiscrepancy}
          onResolve={handleResolve}
          isSubmitting={resolveDiscrepancy.isPending}
        />
      </div>
    </Layout>
  );
};

export default PaymentDiscrepanciesPage;
